import type { UsefulInfoFieldId } from "./tripStore";

export const INFO_NOT_AVAILABLE = "ND";
export const NATIONALITY_DEPENDENT_FIELDS: ReadonlySet<UsefulInfoFieldId> = new Set([
  "embassy", "consulates", "requiredVaccines", "visa", "passport", "internationalDrivingPermit", "healthInsurance"
]);

export interface UsefulInfoSheetReply {
  destinationCode: string;
  global: Partial<Record<UsefulInfoFieldId, string>>;
  byNationality: Record<string, Partial<Record<UsefulInfoFieldId, string>>>;
  checkedAt: string;
}

export interface UsefulInfoTravellingNationality { code: string; label: string }

function normalizedCode(raw: string): string {
  const code = String(raw || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}
function normalizedValue(raw: unknown): string {
  const value = String(raw || "").trim();
  return !value || /^(ND|N\/D|N\/A|DA VERIFICARE|NON DISPONIBILE)$/i.test(value) ? INFO_NOT_AVAILABLE : value;
}
function normalizedKey(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Identical rules are shown once. Divergent rules, including an unknown nationality's
 * ND, are all rendered in the *same* field, with explicit passport labels.
 * A rule documented for Italy must never be copied to a different passport.
 */
export function resolveInfoCell(
  reply: UsefulInfoSheetReply | null,
  field: UsefulInfoFieldId,
  travelers: readonly UsefulInfoTravellingNationality[]
): { value: string; differing: boolean } {
  const global = normalizedValue(reply?.global?.[field]);
  if (!NATIONALITY_DEPENDENT_FIELDS.has(field)) return { value: global, differing: false };
  if (global !== INFO_NOT_AVAILABLE) return { value: global, differing: false };
  if (!travelers.length) return { value: INFO_NOT_AVAILABLE, differing: false };
  const groups = new Map<string, { value: string; labels: string[] }>();
  for (const traveller of travelers) {
    const code = normalizedCode(traveller.code);
    const value = normalizedValue(code ? reply?.byNationality?.[code]?.[field] : undefined);
    const key = normalizedKey(value);
    const label = String(traveller.label || code || "?").trim();
    const group = groups.get(key);
    if (group) {
      if (!group.labels.includes(label)) group.labels.push(label);
    } else groups.set(key, { value, labels: [label] });
  }
  if (groups.size === 1) return { value: [...groups.values()][0].value, differing: false };
  return {
    value: [...groups.values()].map((group) => `${group.labels.join(" / ")}: ${group.value}`).join("\n\n"),
    differing: true
  };
}
